import Breadcrumb from '@/components/feature/Breadcrumb';
import FeedbackForm from '@/pages/about/components/FeedbackForm';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { movies, topics } from '@/lib/movieData';
import { mediaTypeLabels } from '@/types/movie';

const values = [
  { icon: 'ri-compass-3-line', title: '发现优先', text: '我们相信“找到想看的”比“有很多能看”更重要，因此坚持做好分类、筛选与推荐。' },
  { icon: 'ri-shield-check-line', title: '内容真实', text: '页面展示与数据始终保持一致，不做虚假的播放与下载演示，只呈现真实可用的信息。' },
  { icon: 'ri-heart-3-line', title: '用心策划', text: '每个专题都由编辑围绕一个主题精心挑选，帮你看得更成体系、更有方向。' },
  { icon: 'ri-flashlight-line', title: '持续更新', text: '紧跟院线与剧集更新节奏，第一时间把值得关注的新内容带到你面前。' },
];

const scope = [
  { label: '电影', desc: '院线新片、口碑佳作、经典回顾', icon: 'ri-film-line' },
  { label: '电视剧', desc: '热播剧集、高分好剧、海外剧', icon: 'ri-tv-2-line' },
  { label: '综艺', desc: '真人秀、音乐、竞技、生活', icon: 'ri-mic-line' },
  { label: '专题策划', desc: '按主题成体系的内容合集', icon: 'ri-bookmark-3-line' },
];

export default function AboutPage() {
  useDocumentMeta(
    '关于我们 - 久久热影视内容平台',
    '了解久久热的品牌定位、内容范围与品牌故事。久久热致力于为影视爱好者提供清晰、好用的影视资讯与推荐体验。'
  );

  const stats = [
    { value: `${movies.length}`, label: '在库影视作品' },
    { value: `${movies.filter((m) => m.type === 'movie').length}`, label: '电影' },
    { value: `${movies.filter((m) => m.type === 'tv').length}`, label: '电视剧' },
    { value: `${topics.length}`, label: '精选专题' },
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute -right-20 -top-24 w-80 h-80 rounded-full bg-accent-500/10" />
        <div className="absolute left-1/3 bottom-0 w-56 h-56 rounded-full bg-primary-500/20" />
        <div className="relative w-full px-4 md:px-8 py-12 md:py-16">
          <div className="text-background-200/80">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          </div>
          <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider text-accent-400 uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
            关于久久热
          </span>
          <h1 className="mt-3 font-display text-3xl md:text-5xl font-extrabold text-background-50 max-w-3xl leading-tight">
            让每一次“想看点什么”都有答案
          </h1>
          <p className="mt-5 max-w-2xl text-sm md:text-base text-background-100/85 leading-relaxed">
            久久热是一个聚焦影视内容的综合平台，整合最新电影、热门电视剧与综艺的资讯和推荐，
            用清晰的分类、便捷的检索和用心的专题策划，帮助每一位影视爱好者更快找到值得一看的作品。
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {stats.map((s) => (
              <div key={s.label} className="p-4 rounded-lg bg-primary-900/70 border border-primary-800">
                <p className="font-display text-2xl md:text-3xl font-bold text-background-50">{s.value}</p>
                <p className="mt-1 text-xs text-background-200/70">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 品牌故事 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-14 items-start">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950">品牌故事</h2>
            <div className="mt-4 space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                久久热起源于一个很朴素的困扰：好作品越来越多，但“今天到底看什么”反而变得更难。
                我们想做的，其实就是把这个问题变简单一点。
              </p>
              <p>
                所以我们从分类开始，把电影、电视剧与综艺分别梳理清楚；再到筛选，让类型、地区、年份可以自由组合；
                最后是专题，把分散的好内容按主题重新编排，让你可以顺着一个兴趣一路看下去。
              </p>
              <p>
                久久热不追求内容数量的堆砌，而是希望每一个来到这里的用户，都能在几分钟内找到下一次的期待。
              </p>
            </div>

            <div className="mt-7 grid grid-cols-2 gap-4">
              {values.map((v) => (
                <div key={v.title} className="p-4 rounded-lg bg-background-100">
                  <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className={`${v.icon} text-lg`} />
                  </span>
                  <p className="mt-3 text-sm font-semibold text-foreground-950">{v.title}</p>
                  <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{v.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950">内容范围</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              目前久久热覆盖以下内容板块，每一块都配有独立的分类页与筛选维度。
            </p>
            <div className="mt-5 space-y-3">
              {scope.map((s) => (
                <div key={s.label} className="flex items-center gap-4 p-4 rounded-lg border border-background-200 bg-background-50">
                  <span className="w-11 h-11 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                    <i className={`${s.icon} text-xl`} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{s.label}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">{s.desc}</p>
                  </div>
                  <i className="ri-arrow-right-line ml-auto text-foreground-300" />
                </div>
              ))}
            </div>

            <div className="mt-5 p-5 rounded-lg bg-secondary-100 border border-secondary-200">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-secondary-700 text-lg mt-0.5" />
                <p className="text-xs md:text-sm text-secondary-900 leading-relaxed">
                  需要说明的是，久久热目前<strong className="font-semibold">仅提供影视资讯与推荐展示</strong>，
                  不包含登录、在线播放、下载与付费功能，所有内容均为演示数据。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 反馈 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 md:gap-12">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              无论你是想推荐一部好作品，还是对网站体验有想法，我们都非常期待听到你的声音。
            </p>
            <div className="mt-6 space-y-4">
              {[
                { icon: 'ri-mail-line', label: '合作邮箱', value: 'hello@jiujiure.example.com' },
                { icon: 'ri-feedback-line', label: '内容建议', value: 'support@jiujiure.example.com' },
                { icon: 'ri-time-line', label: '回复时间', value: '工作日 24 小时内' },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <span className="w-11 h-11 rounded-md bg-background-50 border border-background-200 text-primary-600 flex items-center justify-center shrink-0">
                    <i className={`${c.icon} text-lg`} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">{c.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {(['movie', 'tv', 'variety'] as const).map((t) => (
                <span key={t} className="h-8 px-3.5 inline-flex items-center rounded-full bg-background-50 border border-background-200 text-sm text-foreground-600">
                  {mediaTypeLabels[t]}频道
                </span>
              ))}
            </div>
          </div>

          <FeedbackForm />
        </div>
      </section>
    </div>
  );
}